import React, { useEffect, useState, createContext, useContext } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { auth, db } from "./config/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import "./App.css";
import SignUpPage from "./components/mainpages/signuppage/SignUpPage";
import LoginPage from "./components/mainpages/loginpage/LoginPage";
import ForgotPassword from "./components/mainpages/forgotpasswordpage/ForgotPassword";
import UserView from "./components/UserView";
import { httpsCallable } from "firebase/functions";
import { functions } from "./config/firebaseConfig";
import LegalPage from "./components/mainpages/legal/LegalPage";
import LandingPage from "./components/mainpages/landingpage/LandingPage";
import { LanguageProvider } from "./contexts/LanguageContext";
import { TrainingProvider, useTraining } from "./contexts/TrainingContext";
import { TimeoutProvider, useTimeout } from "./contexts/TimeoutContext";
import { UnsavedChangesProvider } from "./contexts/UnsavedChangesContext";
import TrainingPopup from "./components/TrainingPopup";
import TimeoutModal from "./components/mainpages/timeout/TimeoutModal";
import CookieConsent from "react-cookie-consent";
import { backgroundImages } from "./components/mainpages/profilepage/BackgroundConfig";

export const UserContext = createContext();

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    role: "",
    uid: "",
    email: "",
    avatar: "Avatar0",
    background: "blob",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const docRef = doc(db, "users", currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData({
            firstName:
              data.firstName ||
              currentUser.displayName?.split(" ")[0] ||
              "DefaultFirstName",
            lastName:
              data.lastName ||
              currentUser.displayName?.split(" ").slice(1).join(" ") ||
              "DefaultLastName",
            role: data.role || "student",
            uid: currentUser.uid,
            email: data.email || currentUser.email,
            avatar: data.avatar || "Avatar0",
            background: data.background || "blob",
            registered: currentUser.metadata.creationTime || null,
            lastLogin: currentUser.metadata.lastSignInTime || null,
          });
        } else {
          // If the user document doesn't exist, create it for OAuth users
          try {
            const createOAuthUserDocument = httpsCallable(
              functions,
              "createOAuthUserDocument"
            );
            const result = await createOAuthUserDocument({
              firstName:
                currentUser.displayName?.split(" ")[0] || "DefaultFirstName",
              lastName:
                currentUser.displayName?.split(" ").slice(1).join(" ") ||
                "DefaultLastName",
              email: currentUser.email,
            });

            if (result.data && result.data.uid) {
              setUserData({
                firstName:
                  currentUser.displayName?.split(" ")[0] || "DefaultFirstName",
                lastName:
                  currentUser.displayName?.split(" ").slice(1).join(" ") ||
                  "DefaultLastName",
                role: "student",
                uid: currentUser.uid,
                email: currentUser.email,
                avatar: "Avatar0",
                background: "blob",
                registered: currentUser.metadata.creationTime || null,
                lastLogin: currentUser.metadata.lastSignInTime || null,
              });
            } else {
              throw new Error("Failed to create OAuth user document");
            }
          } catch (error) {
            console.error("Error creating OAuth user document:", error);
            // Handle the error appropriately (e.g., show an error message to the user)
          }
        }
      } else {
        setUser(null);
        setUserData({
          firstName: "",
          lastName: "",
          role: "",
          uid: "",
          background: "blob",
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <UserContext.Provider value={{ ...userData, loading, setUserData }}>
      <LanguageProvider>
        <TrainingProvider>
          <TimeoutProvider>
            <UnsavedChangesProvider>
              <Router>
                <div className="App">
                  <CookieConsent
                    location="bottom"
                    buttonText="I Understand"
                    cookieName="socrateslab-cookie-consent"
                    style={{
                      background: "#253343",
                      padding: "1rem",
                      fontSize: "14px",
                      alignItems: "center",
                    }}
                    buttonStyle={{
                      background: "#4CAF50",
                      color: "white",
                      fontSize: "14px",
                      borderRadius: "4px",
                      padding: "10px 20px",
                    }}
                    expires={365}
                  >
                    This platform uses essential cookies necessary for
                    authentication and basic functionality. By using
                    SocratesLab, you acknowledge our use of these essential
                    cookies. See our{" "}
                    <a
                      href="/legal?section=cookies"
                      style={{ color: "white", textDecoration: "underline" }}
                    >
                      Cookie Policy
                    </a>{" "}
                    for more details.
                  </CookieConsent>
                  <MainApp user={user} role={userData.role} />
                  <TrainingPopupManager />
                  {user && <TimeoutManager />}
                </div>
              </Router>
            </UnsavedChangesProvider>
          </TimeoutProvider>
        </TrainingProvider>
      </LanguageProvider>
    </UserContext.Provider>
  );
}

function MainApp({ user, role }) {
  const location = useLocation();
  const userData = useContext(UserContext);
  const { isTrainingEnabled } = useTraining();

  useEffect(() => {
    const isLoginOrSignup =
      location.pathname === "/login" || location.pathname === "/signup";

    if (isLoginOrSignup) {
      document.body.className = "login-signup-background";
    } else {
      // Use user's selected background or default to blob
      const selectedBackground = userData.background || "blob";
      const backgroundImage = backgroundImages[selectedBackground];

      document.body.className = "default-background";
      document.body.style.backgroundImage = `url(${backgroundImage})`;
    }
  }, [location, userData.background]);

  return (
    <div
      className={`app ${
        isTrainingEnabled ? "training-enabled" : "training-disabled"
      }`}
    >
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/legal" element={<LegalPage />} />
        {user ? (
          <Route path="/*" element={<UserView />} />
        ) : (
          <Route path="*" element={<Navigate to="/" />} />
        )}
      </Routes>
    </div>
  );
}

// Training popup manager component
function TrainingPopupManager() {
  const { currentTip, closeTip, dismissTip } = useTraining();

  const handleDismiss = (tipId) => {
    const callback = currentTip?.onDismissCallback;
    dismissTip(tipId);
    // Call the callback if it exists
    if (callback && typeof callback === "function") {
      setTimeout(callback, 100); // Small delay to ensure tip is fully dismissed
    }
  };

  const handleClose = () => {
    const callback = currentTip?.onDismissCallback;
    closeTip();
    // Call the callback if it exists (user closed without marking as "got it")
    if (callback && typeof callback === "function") {
      setTimeout(callback, 100); // Small delay to ensure tip is fully closed
    }
  };

  return (
    <TrainingPopup
      tip={currentTip}
      onClose={handleClose}
      onDismiss={handleDismiss}
      targetElement={currentTip?.targetElement}
      position={currentTip?.position || "center"}
    />
  );
}

// Timeout manager component
function TimeoutManager() {
  const { isTimeoutModalVisible, countdown, resetActivity, handleTimeout } =
    useTimeout();
  const navigate = useNavigate();

  const handleTimeoutComplete = async () => {
    try {
      await handleTimeout();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  useEffect(() => {
    if (countdown <= 1 && isTimeoutModalVisible) {
      handleTimeoutComplete();
    }
  }, [countdown, isTimeoutModalVisible]);

  return (
    <TimeoutModal
      isVisible={isTimeoutModalVisible}
      countdown={countdown}
      onStillHere={resetActivity}
    />
  );
}

export default App;
